/**
 * ModelTileLayout — the 2D workbench's model-space viewport tiling shell
 * (Faz 4.9, OCS pane_grid parity without MDI).
 *
 * Configurations mirror the OCS VPORTS set (SIngle/2H/2V/4). The ACTIVE
 * cell hosts the interactive viewport (passed as `active` — the tool scope,
 * osnap pipeline and command target all stay owned by that single mount);
 * the remaining cells render live read-only previews with their own
 * pan/zoom (Cad2DCellViewport). Clicking a preview cell makes it active,
 * which moves the interactive mount — the command line always drives the
 * last-clicked cell, like OCS's pane_grid focus.
 *
 * Faz 10.5 named VPORTS: the store's join overlay (`spans`) merges adjacent
 * base-grid slots into one viewport — each group renders as a single box
 * spanning the union of its member slots.
 */

import { Box } from '@chakra-ui/react';
import {
    useModelViewportsStore,
    VPORTS_GRID,
    VPORTS_CELL_COUNT,
    identitySpans,
    type ModelVportsConfig,
} from '../../state/useModelViewportsStore';
import { Cad2DCellViewport } from './Cad2DCellViewport';

/** Cell slot rects as CSS inset fractions (dynamic positioning only). */
interface CellRect { left: string; top: string; width: string; height: string }

const GAP = 3;

/** One rendered viewport: the base-grid slots it covers + its box. */
interface SlotGroup { slots: number[]; rect: CellRect }

/** Rect of one group of slots inside the config's cols×rows base grid.
 *  Grids are at most 2×2, so edge insets only ever need the GAP/2 term. */
function groupRect(config: ModelVportsConfig, slots: number[]): CellRect {
    const { cols, rows } = VPORTS_GRID[config];
    const colMin = Math.min(...slots.map((s) => s % cols));
    const colMax = Math.max(...slots.map((s) => s % cols));
    const rowMin = Math.min(...slots.map((s) => Math.floor(s / cols)));
    const rowMax = Math.max(...slots.map((s) => Math.floor(s / cols)));
    const spanCols = colMax - colMin + 1;
    const spanRows = rowMax - rowMin + 1;
    const edge = (n: number, total: number, axis: 'w' | 'h'): string => {
        if (total <= 1) return axis === 'w' ? '100%' : '100%';
        if (n === total) return '100%';
        // A partial span: shrink by half a gap on the dividing edge only.
        return `calc(${(n / total) * 100}% - ${GAP / 2}px)`;
    };
    return {
        left: colMin === 0 ? '0' : `calc(${(colMin / cols) * 100}% + ${GAP / 2}px)`,
        top: rowMin === 0 ? '0' : `calc(${(rowMin / rows) * 100}% + ${GAP / 2}px)`,
        width: edge(spanCols, cols, 'w'),
        height: edge(spanRows, rows, 'h'),
    };
}

/** Join overlay → rendered viewport groups (order: first slot ascending). */
function slotGroups(config: ModelVportsConfig, spans: number[][]): SlotGroup[] {
    const count = VPORTS_CELL_COUNT[config];
    const groups = (spans.length ? spans : identitySpans(config))
        .map((slots) => slots.filter((s) => s >= 0 && s < count))
        .filter((slots) => slots.length > 0)
        .sort((a, b) => a[0] - b[0]);
    return groups.map((slots) => ({ slots, rect: groupRect(config, slots) }));
}

export function ModelTileLayout({ active }: { active: React.ReactNode }) {
    const config = useModelViewportsStore((s) => s.config);
    const activeCell = useModelViewportsStore((s) => s.activeCell);
    const spans = useModelViewportsStore((s) => s.spans);
    const groups = slotGroups(config, spans);

    return (
        <Box w="100%" h="100%" position="relative" bg="border.base">
            {groups.map((group) => {
                const isActive = group.slots.includes(activeCell);
                return (
                    <Box
                        // Slot-set identity keeps the interactive mount alive
                        // across geometry changes; the preview key includes the
                        // first slot so a joined cell re-mounts its preview.
                        key={isActive ? 'active' : `g${group.slots.join('-')}`}
                        position="absolute"
                        overflow="hidden"
                        borderRadius="sm"
                        left={group.rect.left}
                        top={group.rect.top}
                        width={group.rect.width}
                        height={group.rect.height}
                    >
                        {isActive
                            // The interactive mount never re-parents across cells
                            // during one tiling session: React keeps the subtree
                            // mounted because the slot element identity persists
                            // — only its geometry moves.
                            ? active
                            : <Cad2DCellViewport cell={group.slots[0]} />}
                    </Box>
                );
            })}
        </Box>
    );
}
